import { BaseFootprint } from '../../domain/models/locations/BaseFootprint';
import { BaseLocation } from '../../domain/models/locations/BaseLocation';
import { GoodsByCategory } from '../../domain/models/locations/GoodsByCategory';
import { LocationExternalSource } from '../../domain/models/locations/LocationExternalSource';
import { LocationEvent } from '../../domain/models/locations/LocationEvent';
import { SimpleArtist } from '../../domain/models/locations/SimpleArtist';
import { SimpleMember } from '../../domain/models/locations/SimpleMember';
import { Tweet } from '../../domain/models/locations/Tweet';
import type { ILocationEventDetailRepository } from '../../domain/repositories/ILocationEventDetailRepository';
import type { ILocationEventDetailResponseBody } from '../../types/response/ILocationEventDetailResponseBody';
import { queryGet, queryPost } from '@pocapan/shared/utils/queryClient';

export class LocationEventDetailRepository implements ILocationEventDetailRepository {
  async getLocationEventDetail(locationId: string, eventId: string): Promise<LocationEvent> {
    const url = `/api/v1/locations/${locationId}/events/${eventId}`;
    try {
      const result = await queryGet(url);
      const responseData = (result?.event ?? result) as ILocationEventDetailResponseBody;
      const mapped = this.mapToLocationEventDetail(responseData);
      return mapped;
    } catch (_error) {
      throw new Error('Error fetching location event detail');
    }
  }

  async toggleLike(locationId: string): Promise<boolean> {
    try {
      const url = `/api/v1/locations/${locationId}/like`;
      const result = await queryPost(url);
      return result.isLiked || false;
    } catch (_error) {
      throw new Error('Error toggling like status');
    }
  }

  private mapToLocationEventDetail(data: ILocationEventDetailResponseBody): LocationEvent {
    const artist = SimpleArtist.fromResponse(data.artist);
    const members = (data.members ?? []).map((member) => SimpleMember.fromResponse(member));

    const location = new BaseLocation(
      data.location.locationId,
      data.location.kakaoUid,
      data.location.naverUid,
      data.location.googleUid,
      data.location.name,
      data.location.description,
      data.location.category,
      data.location.siteType,
      data.location.streetAddress,
      data.location.lotAddress,
      data.location.latitude,
      data.location.longitude,
      data.location.likesCount,
      data.location.postsCount,
      data.location.distanceInMeters,
      data.location.isLiked,
      data.location.isLandmark,
      data.location.isShutdown,
      (data.location.footprints ?? []).map((footprint) => BaseFootprint.fromResponse(footprint))
    );

    const goodsByCategory = new GoodsByCategory(
      data.goodsByCategory?.event ?? [],
      data.goodsByCategory?.photo ?? [],
      data.goodsByCategory?.storage ?? [],
      data.goodsByCategory?.accessory ?? [],
      data.goodsByCategory?.decoration ?? []
    );

    return new LocationEvent(
      data.eventId,
      data.name,
      data.locationId,
      new Date(data.startDate),
      new Date(data.endDate),
      data.category,
      data.status,
      data.countryEmoji,
      data.countryName,
      data.goodsNames ?? [],
      artist,
      members,
      data.baseImageUrl ?? '',
      data.organizerTwitterId ?? '',
      data.posterImageUrls ?? [],
      data.goodsGiftTypes ?? [],
      goodsByCategory,
      data.tweet ? Tweet.fromResponse(data.tweet) : undefined,
      location,
      data.description ?? '',
      data.externalSource ? LocationExternalSource.fromResponse(data.externalSource) : undefined
    );
  }
}
